import useQueryStringParameters from '../../../utils/useQueryStringParameters';
import {
  buildSearchPerformanceQueryParameters,
  QueryParamNames,
  type SearchPerformanceQueryParams,
} from '../SearchPerformanceQueryParameters';
import { SEARCH_PERFORMANCE_ROUTE } from '../SearchPerformanceRoutes';
import type { PagesTabKey } from './PagesTabs';

/** Scroll anchor on the section root, for same-page entry from other sections. */
export const PAGES_SECTION_ID = 'search-performance-pages';

export type PagesSectionTarget = {
  /** KPI CTAs set this; Review omits it so the current tab stays. */
  tab?: PagesTabKey;
  /** Review sets this (the row's display path); KPI CTAs omit it so a leftover search is dropped. */
  search?: string;
};

/** `docs` → `/docs/`, `uncategorized` → `/`. */
export function formatSectionPath(sectionKey: string): string {
  return sectionKey === 'uncategorized' ? '/' : `/${sectionKey}/`;
}

/**
 * Href for a link into the Pages section. `tab` and `search` are independent:
 * omitting `tab` leaves it, omitting `search` from a tab CTA clears it.
 */
export function buildPagesSectionHref(currentQueryString: string, target: PagesSectionTarget): string {
  const params = new URLSearchParams(currentQueryString);
  if (target.tab !== undefined) {
    params.set(QueryParamNames.Tab, target.tab);
    if (target.search === undefined) params.delete(QueryParamNames.Search);
  }
  if (target.search !== undefined) params.set(QueryParamNames.Search, target.search);
  return `${SEARCH_PERFORMANCE_ROUTE}?${params.toString()}`;
}

function scrollToPagesSection() {
  // After the param-change commit (prototype's timing): scrolling in the same
  // task races the re-render — the table collapses to a skeleton, the page
  // shortens, and the smooth animation gets clamped short of the section.
  setTimeout(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document
      .getElementById(PAGES_SECTION_ID)
      ?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, 0);
}

/**
 * Same-page entry into Pages. KPI CTAs pass `tab` (and drop `search`); Review
 * passes `search` (and leaves `tab`) — the effect is the user having typed the
 * row's path into the Pages URL search. `property` is preserved because the
 * util copies the current search.
 */
export function usePagesSectionNavigation(): (target: PagesSectionTarget) => void {
  const { updateMultipleParameters } = useQueryStringParameters<SearchPerformanceQueryParams>(
    buildSearchPerformanceQueryParameters
  );

  return (target: PagesSectionTarget) => {
    if (target.tab !== undefined) {
      updateMultipleParameters({ tab: target.tab, search: target.search });
    } else {
      updateMultipleParameters({ search: target.search });
    }
    scrollToPagesSection();
  };
}
